import { useEffect, useState } from 'react';
import { particularTravelService } from '../service/particularTravelService';
import { useSuperMutation, useSuperQuery } from '@vadiun/react-hooks-legacy';
import {
  ParticularTravelsFiltersFormType,
  ParticularTravelStatus,
} from '../models/particularTravelsModel';
import {
  loadParticularStateFromLocalStorage,
  getDefaultColumnVisibility,
  getDefaultFilters,
  getDefaultSort,
  ParticularColumnVisibility,
  saveParticularStateToLocalStorage
} from '../utils/particularStorage';

export { columnsLabels, getDefaultFilters } from '../utils/particularStorage';

export const useParticularTravel = () => {
  const ParticularTravelService = particularTravelService();
  const [state, setState] = useState(loadParticularStateFromLocalStorage);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    saveParticularStateToLocalStorage(state);
  }, [state]);

  const getParticularTravels = useSuperQuery(
    () =>
      ParticularTravelService.getAll(
        state.page,
        state.rowsPerPage,
        state.sort.sortBy,
        state.sort.sortDir,
        state.filters
      ),
    {
      showSpinner: true
    },
    [
      state.page,
      state.rowsPerPage,
      state.sort.sortBy,
      state.sort.sortDir,
      JSON.stringify({
        ...state.filters,
        dateFrom: state.filters.dateFrom
          ? state.filters.dateFrom.toISOString()
          : null,
        dateTo: state.filters.dateTo ? state.filters.dateTo.toISOString() : null
      })
    ]
  );

  const onChangePage = (newPage: number) => {
    if (newPage !== state.page) {
      setState((prev) => ({ ...prev, page: newPage }));
    }
  };

  const onChangeRowsPerPage = (newRowsPerPage: number) => {
    setState((prev) => ({
      ...prev,
      rowsPerPage: newRowsPerPage,
      page: 1
    }));
  };

  const onSortChange = (direction: string | null, sortBy: string) => {
    setState((prev) => ({
      ...prev,
      sort: {
        sortDir: direction === 'desc' ? 'DESC' : 'ASC',
        sortBy
      },
      page: 1
    }));
  };

  const onFiltersChange = (filtersEntry: ParticularTravelsFiltersFormType) => {
    const shouldResetSelection =
      (state.filters.state === false &&
        filtersEntry.state === ParticularTravelStatus.FACTURADO) ||
      (state.filters.state === ParticularTravelStatus.FACTURADO &&
        filtersEntry.state === false);

    setState((prev) => ({
      ...prev,
      filters: filtersEntry,
      page: 1
    }));

    if (shouldResetSelection) {
      setSelectedIds([]);
    }
  };

  const setColumnVisibility = (
    newColumnVisibility: ParticularColumnVisibility
  ) => {
    setState((prev) => ({
      ...prev,
      columnVisibility: newColumnVisibility
    }));
  };

  const resetState = () => {
    setState({
      page: 1,
      rowsPerPage: 10,
      sort: getDefaultSort(),
      filters: getDefaultFilters(),
      columnVisibility: getDefaultColumnVisibility()
    });
  };
  const onManualPayment = useSuperMutation(
    (
      travelId: number,
      payment_method_id: number,
      transaction_number: string,
      notes: string
    ) =>
      ParticularTravelService.manualPayment(
        travelId,
        payment_method_id,
        transaction_number,
        notes
      ),
    { showSpinner: true }
  );

  const onBill = useSuperMutation(
    (number: string, letter: string) =>
      ParticularTravelService.bill(selectedIds, number, letter),
    { showSpinner: true }
  );

  const onSendBilling = useSuperMutation(
    (travelIds: number[]) => ParticularTravelService.sendBilling(travelIds),
    { showSpinner: true }
  );




  const reload = () =>
    getParticularTravels.reload().then(() => undefined);

  return {
    particularTravels: {
      data: getParticularTravels.data?.rows || [],
      total: getParticularTravels.data?.total || 0,
      isLoading: getParticularTravels.isLoading,
      error: getParticularTravels.error,
      serverSideOptions: {
        page: state.page - 1,
        count: getParticularTravels.data?.total || 0,
        rowsPerPage: state.rowsPerPage,
        sortOrder: {
          name: state.sort.sortBy,
          direction: state.sort.sortDir.toLowerCase() as 'asc' | 'desc'
        }
      }
    },
    reload: getParticularTravels.reload,
    filters: state.filters,
    page: state.page,
    rowsPerPage: state.rowsPerPage,
    columnVisibility: state.columnVisibility,
    setColumnVisibility,
    resetState,
    onChangePage,
    onChangeRowsPerPage,
    onSortChange,
    onFiltersChange,
    onManualPayment,
    onBill,
    onSendBilling,
    selectedIds,
    setSelectedIds,
  };
};